import {
    faFileLines,
    faChartLine,
    faFileArrowDown,
    faBrain,
    faDatabase,
    faCartShopping,
    faEnvelope,
    faServer,
    faList,
    faEnvelopeOpenDollar,
    faGaugeMax,
    faCartXmark,
    faUserTag,
    faCrosshairs,
    faCalendar,
    faCalendarDays,
    faMagnifyingGlass,
    faHashtag,
    faEye,
    faFile,
    faTrash,
    faMessage,
    faArrowsSplitUpAndLeft,
    faArrowRotateRight,
    faChartSimple,
    faGaugeHigh,
    faArrowTrendUp,
    faTrophy,
    faUpload,
    faTableCells,
    faCalculator,
    faFileWord,
    faFilePdf,
    faBookmark,
    faIdBadge,
    faClock,
    faInbox,
    faReply,
    faPaperclip,
    faEnvelopeCircleCheck,
    faTag,
} from '@fortawesome/pro-regular-svg-icons';

import { withIcon } from '../core/components/Icon.tsx';

export type PromptCategoryKey =
    | 'casesAndDocuments'
    | 'analytics'
    | 'fileCreation'
    | 'memory'
    | 'data'
    | 'offersAndOrders'
    | 'email'
    | 'liveErpData';

// order here is the display order on the extended library page (filter tabs and sections)
export const PROMPT_LIBRARY_CATEGORIES: readonly PromptCategoryKey[] = [
    'casesAndDocuments',
    'analytics',
    'fileCreation',
    'memory',
    'data',
    'offersAndOrders',
    'email',
    'liveErpData',
];

export const PROMPT_CATEGORY_META: Record<PromptCategoryKey, { icon: ReturnType<typeof withIcon>; locked?: boolean }> =
    {
        casesAndDocuments: { icon: withIcon(faFileLines) },
        analytics: { icon: withIcon(faChartLine) },
        fileCreation: { icon: withIcon(faFileArrowDown) },
        memory: { icon: withIcon(faBrain) },
        data: { icon: withIcon(faDatabase) },
        offersAndOrders: { icon: withIcon(faCartShopping) },
        email: { icon: withIcon(faEnvelope), locked: true },
        liveErpData: { icon: withIcon(faServer), locked: true },
    };

export const FEATURED_PROMPTS: {
    category: PromptCategoryKey;
    promptId: string;
    requiresAttachment?: boolean;
    icon: ReturnType<typeof withIcon>;
}[] = [
    {
        category: 'casesAndDocuments',
        promptId: 'processUploadedFileType',
        requiresAttachment: true,
        icon: withIcon(faList),
    },
    { category: 'offersAndOrders', promptId: 'openOffersForCustomer', icon: withIcon(faEnvelopeOpenDollar) },
    { category: 'analytics', promptId: 'automationRateInPeriod', icon: withIcon(faGaugeMax) },
    { category: 'casesAndDocuments', promptId: 'documentsDiscardedInPeriod', icon: withIcon(faCartXmark) },
    { category: 'analytics', promptId: 'strongestCustomerInPeriod', icon: withIcon(faUserTag) },
];

export const PROMPTS_REQUIRING_ATTACHMENT: ReadonlySet<string> = new Set(['processUploadedFileType']);

export const PROMPT_ICONS: Record<string, ReturnType<typeof withIcon>> = {
    // Cases & documents
    ordersCurrentlyProcessing: withIcon(faEnvelope),
    documentTypeNeedsSupport: withIcon(faCrosshairs),
    ordersCompletedSinceDate: withIcon(faCalendar),
    findOrdersByCustomerArticle: withIcon(faMagnifyingGlass),
    ordersCompletedSuccessfullyInPeriod: withIcon(faHashtag),
    readOrderByCaseId: withIcon(faEye),
    showOriginalDocumentByCaseId: withIcon(faFile),
    discardOrderWithReason: withIcon(faTrash),
    createOrderFromMessage: withIcon(faMessage),
    processUploadedFileAsOrder: withIcon(faFileLines),
    splitPdfIntoCases: withIcon(faArrowsSplitUpAndLeft),
    createOrderCaseFromOriginal: withIcon(faArrowRotateRight),
    // Analytics
    ordersProcessedInYearValue: withIcon(faChartSimple),
    automationRateForOrdersInPeriod: withIcon(faGaugeHigh),
    compareOrdersPerMonth: withIcon(faArrowTrendUp),
    topFiveCustomersByOrderValue: withIcon(faTrophy),
    highestValueOrderInYear: withIcon(faChartLine),
    summarizeAttachedFile: withIcon(faUpload),
    // File creation
    exportResultsAsExcel: withIcon(faTableCells),
    exportLineItemsWithCalculation: withIcon(faCalculator),
    createWordDocFromAnalysis: withIcon(faFileWord),
    summarizeCasesAsPdf: withIcon(faFilePdf),
    createPdfRecordOfConversation: withIcon(faFilePdf),
    // Memory
    rememberMainChannel: withIcon(faBookmark),
    whatHaveYouRemembered: withIcon(faBrain),
    // Data
    articleNumberOfProduct: withIcon(faDatabase),
    searchCustomerInCity: withIcon(faEye),
    customerNumberOfCompany: withIcon(faIdBadge),
    productsSuitableForRequirement: withIcon(faEye),
    // Offers & orders
    offerDetailsByNumber: withIcon(faMagnifyingGlass),
    openOrdersWithSupplier: withIcon(faEye),
    openOrderByNumber: withIcon(faFile),
    customerOrdersInMonth: withIcon(faClock),
    // Email
    inboxNewToday: withIcon(faInbox),
    replyWithDeliveryDate: withIcon(faReply),
    attachFileToReply: withIcon(faPaperclip),
    markEmailRead: withIcon(faEnvelopeCircleCheck),
    // Live ERP data
    articlePriceForCustomer: withIcon(faTag),
    articleAvailability: withIcon(faEye),
    articleDeliveryDate: withIcon(faCalendarDays),
    articleTrackingLink: withIcon(faEye),
};

export const DOCUMENT_TYPE_PLACEHOLDER_PROMPTS: Record<string, 'singular' | 'plural'> = {
    processUploadedFileType: 'singular',
    documentTypeNeedsSupport: 'plural',
    ordersCurrentlyProcessing: 'plural',
    ordersCompletedSinceDate: 'plural',
    findOrdersByCustomerArticle: 'plural',
    ordersCompletedSuccessfullyInPeriod: 'plural',
    ordersProcessedInYearValue: 'plural',
    automationRateForOrdersInPeriod: 'plural',
    compareOrdersPerMonth: 'plural',
    openOrdersWithSupplier: 'plural',
};

export const CHANNEL_FIELD_DOCUMENT_TYPE_LABELS: Partial<Record<string, { singular: string; plural: string }>> = {
    orderProcessingChannels: { singular: 'an order', plural: 'orders' },
    invoiceProcessingChannels: { singular: 'an invoice', plural: 'invoices' },
    orderConfirmationProcessingChannels: { singular: 'an order confirmation', plural: 'order confirmations' },
    rfqProcessingChannels: { singular: 'a price request', plural: 'price requests' },
    deliveryNoteProcessingChannels: { singular: 'a delivery note', plural: 'delivery notes' },
    propertyBillProcessingChannels: { singular: 'a property bill', plural: 'property bills' },
};

export const resolveSingleDocumentType = (
    channelsData: Record<string, { length: number }[] | undefined> | undefined,
    channelFields: readonly string[]
): { singular: string; plural: string } | null => {
    if (!channelsData) return null;
    const fieldsWithChannels = channelFields.filter((field) => (channelsData[field]?.length ?? 0) > 0);
    if (fieldsWithChannels.length !== 1) return null;
    return CHANNEL_FIELD_DOCUMENT_TYPE_LABELS[fieldsWithChannels[0]] ?? null;
};

export const applyDocumentTypePersonalization = (
    promptId: string,
    text: string,
    documentType: { singular: string; plural: string } | null
): string => {
    const form = DOCUMENT_TYPE_PLACEHOLDER_PROMPTS[promptId];
    if (!form || !documentType) return text;
    return form === 'singular'
        ? text.replace('an [document type]', documentType.singular)
        : text.replace('[document type]', documentType.plural);
};

export type PromptCategoryTranslations = Record<string, { label: string; prompts: { id: string; text: string }[] }>;

export interface FeaturedPrompt {
    promptId: string;
    category: PromptCategoryKey;
    text: string;
    label: string;
    icon: ReturnType<typeof withIcon>;
    requiresAttachment: boolean;
}

export const resolveFeaturedPrompts = (
    categories: PromptCategoryTranslations,
    documentType: { singular: string; plural: string } | null = null
): FeaturedPrompt[] =>
    FEATURED_PROMPTS.flatMap((featured) => {
        const prompt = categories[featured.category]?.prompts.find((p) => p.id === featured.promptId);
        if (!prompt) return [];
        return [
            {
                promptId: featured.promptId,
                category: featured.category,
                text: applyDocumentTypePersonalization(prompt.id, prompt.text, documentType),
                label: categories[featured.category].label,
                icon: featured.icon ?? PROMPT_CATEGORY_META[featured.category].icon,
                requiresAttachment: Boolean(featured.requiresAttachment),
            },
        ];
    });
